<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { NButton, NEmpty, NInput, NModal } from 'naive-ui'
import type { TreeOption } from 'naive-ui'
import AppTreeView from '@/components/AppTreeView.vue'
import {
  collectStoryPickerExpandKeys,
  filterStoryTree,
  isStoryMapIdInList,
  sceneTreeLabel,
} from '@/features/story/storyTreeProjection'
import { useSceneEditorStore } from '@/store/sceneEditor'
import { blurModalSearchFocus } from '@/utils/blurModalSearchFocus'

const props = defineProps<{
  show: boolean
  title?: string
  selectedId: number
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  select: [value: number]
}>()

const editor = useSceneEditorStore()
const searchText = ref('')
const currentId = ref(0)
const expandedKeys = ref<Array<string | number>>([])
const treeWrapRef = ref<HTMLElement | null>(null)
let applyingExpandKeys = false

const modalTitle = computed(() => props.title?.trim() || '选择剧情')

const filteredStoryTree = computed(() => {
  const q = searchText.value.trim().toLowerCase()
  if (!q) return editor.storyTreeData
  return filterStoryTree(editor.storyTreeData, q)
})

const selectedSceneKeys = computed<Array<string | number>>(() =>
  currentId.value > 0 ? [currentId.value] : [],
)

const selectedScene = computed(() => {
  const item = editor.storyTreeList.find((map) => Number(map.Id) === currentId.value)
  return item ? sceneTreeLabel(item) : ''
})

function closeModal() {
  emit('update:show', false)
}

function confirmSelect() {
  if (!selectedScene.value) return
  emit('select', currentId.value)
  closeModal()
}

function selectAndConfirm(id: number) {
  if (!Number.isFinite(id) || id <= 0) return
  currentId.value = id
  emit('select', id)
  closeModal()
}

function onStoryTreeSelect(keys: Array<string | number>) {
  const nextId = Number(keys[0])
  if (Number.isFinite(nextId) && nextId > 0) currentId.value = nextId
}

function onExpandedKeysUpdate(keys: Array<string | number>) {
  if (applyingExpandKeys) return
  expandedKeys.value = keys
}

function storyPickerNodeProps({ option }: { option: TreeOption }) {
  const sceneId = Number(option.key)
  return {
    'data-scene-key': String(option.key ?? ''),
    onDblclick: (event: MouseEvent) => {
      event.stopPropagation()
      selectAndConfirm(sceneId)
    },
  }
}

/** 写入选中剧情与展开链（忽略树控件紧随其后的空回写）。 */
function applyPickerState(mapId: number) {
  const id = Math.round(Number(mapId) || 0)
  const resolvedId = isStoryMapIdInList(id, editor.storyTreeList) ? id : 0
  currentId.value = resolvedId
  applyingExpandKeys = true
  expandedKeys.value =
    resolvedId > 0 ? collectStoryPickerExpandKeys(resolvedId, editor.storyTreeList) : []
  void nextTick(() => {
    applyingExpandKeys = false
  })
}

async function scrollSelectedIntoView() {
  await nextTick()
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })
  const wrap = treeWrapRef.value
  if (!wrap || currentId.value <= 0) return
  const key = CSS.escape(String(currentId.value))
  const target =
    wrap.querySelector<HTMLElement>(`.n-tree-node--selected [data-scene-key="${key}"]`) ??
    wrap.querySelector<HTMLElement>(`[data-scene-key="${key}"]`)
  target?.scrollIntoView({ block: 'nearest' })
}

watch(
  () => props.show,
  (show) => {
    if (!show) return
    searchText.value = ''
    applyPickerState(props.selectedId)
  },
  { immediate: true },
)

watch(
  () => props.selectedId,
  (mapId) => {
    if (props.show) applyPickerState(mapId)
  },
)

function onModalAfterEnter() {
  blurModalSearchFocus('.story-picker-modal')
  void scrollSelectedIntoView()
}
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    class="story-picker-modal"
    :title="modalTitle"
    :auto-focus="false"
    :style="{ width: '720px' }"
    @after-enter="onModalAfterEnter"
    @update:show="emit('update:show', $event)"
  >
    <div class="story-picker">
      <n-input
        v-model:value="searchText"
        size="small"
        clearable
        :autofocus="false"
        placeholder="输入剧情 ID 或名称快速查找"
      />

      <div v-if="filteredStoryTree.length" ref="treeWrapRef" class="story-picker__tree-wrap">
        <AppTreeView
          class="story-picker__tree"
          :data="filteredStoryTree"
          :expanded-keys="expandedKeys"
          :selected-keys="selectedSceneKeys"
          :node-props="storyPickerNodeProps"
          @update:expanded-keys="onExpandedKeysUpdate"
          @update:selected-keys="onStoryTreeSelect"
        />
      </div>
      <n-empty v-else description="没有匹配的剧情" size="small" />

      <div class="story-picker__summary">
        {{ selectedScene ? `当前选择：${selectedScene}` : '未选择剧情' }}
      </div>

      <div class="story-picker__actions">
        <n-button size="small" @click="closeModal">取消</n-button>
        <n-button size="small" type="primary" :disabled="!selectedScene" @click="confirmSelect">确定</n-button>
      </div>
    </div>
  </n-modal>
</template>

<style scoped>
.story-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.story-picker__tree-wrap {
  height: 420px;
  min-height: 240px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  overflow: hidden;
}

.story-picker__tree {
  height: 100%;
}

.story-picker__summary {
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.story-picker__actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding-top: 4px;
  border-top: 1px solid var(--theme-border);
}
</style>
